import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { api } from "../api";
import { isSystemMessage, parseSystemMessage } from "@butler/shared";
import type { ModelName } from "@butler/shared";
import { ChatInterface } from "../components/Chat/ChatInterface";
import type {
  Message,
  StreamingWord,
  ChatControllerProps,
} from "@/lib/ChatModel";
import useLocalStorage from "../lib/localStorage";
import useAsync from "@/lib/useAsync";
import { toast } from "sonner";
import { useWorkflow } from "@/lib/useWorkflow";
export default function ChatController({
  id,
  initialMessages = [],
}: ChatControllerProps) {
  const urlParams = useParams();
  const workflow = useWorkflow();
  const [title, setTitle] = useState<string>(workflow?.name || "");
  const [queryParams, _setQueryParams] = useSearchParams();
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useLocalStorage<ModelName>(
    "selectedModel",
    "mock",
  );

  const [streamingWords, setStreamingWords] = useState<StreamingWord[]>([]);
  const navigate = useNavigate();

  const chatId = id || urlParams.id;

  useEffect(() => {
    if (workflow) {
      setTitle(workflow.name);
    }
  }, [workflow]);

  const {
    data: formattedMessages,
    error: loadError,
    isLoading,
  } = useAsync(async () => {
    if (!chatId || chatId === "new") {
      return [];
    }
    // Fetch chat history from API
    const response = await api.chat({ id: chatId }).get();
    if (!response || !response.data) {
      throw new Error("An error occurred while fetching chat history.");
    }
    if (!response.data?.length) {
      return [];
    }
    // Convert to our message format with unique IDs
    const formattedMessages: Message[] = response.data.map((message) => ({
      id: `${message.role}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      role: message.role,
      content: message.content,
      type: message.role === "user" ? "user" : "system",
      completed: true,
    }));

    return formattedMessages;
  }, [chatId]);

  useEffect(() => {
    const query = queryParams.get("query");
    if (query) {
      console.log("query", query);
      handleMessageSubmit(query);
    }
  }, [queryParams]);

  useEffect(() => {
    setMessages(formattedMessages || []);
  }, [formattedMessages]);

  useEffect(() => {
    if (loadError) {
      toast(loadError.message);
    }
    if (error) {
      toast(error);
    }
  }, [loadError, error]);

  // Function to handle message submission with word-by-word streaming
  const handleMessageSubmit = async (content: string) => {
    try {
      // Add user message
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: "user",
        content,
        type: "user",
        completed: true,
        newSection: messages.length > 0,
      };

      // Create initial assistant message
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: "",
        type: "system",
        completed: false,
      };

      // Add both messages to state
      setMessages((prev) => [...prev, userMessage, assistantMessage]);

      // Start the API call
      const { data: responseStream, error } = await api
        .chat({ id: chatId || "new" })
        .post({
          prompt: content,
          model: selectedModel as string,
        });

      if (error) {
        throw error;
      }

      let accumulatedContent = "";
      let currentWordId = 0;
      let tempId = chatId;

      for await (const chunk of responseStream) {
        console.log("chunk", chunk);
        if (isSystemMessage(chunk)) {
          const { type, value } = parseSystemMessage(chunk);
          switch (type) {
            case "log:ERROR":
              setError(value);
              throw new Error(value);
            case "set:ID":
              tempId = value;
              break;
            case "set:NAME":
              setTitle(value);
              break;
          }
        } else {
          accumulatedContent += chunk;

          // Split the new content into words for animation
          let words = [chunk.toString()];
          if (typeof chunk === "string" && chunk.includes(" ")) {
            words = chunk.split(" ");
          }
          const newStreamingWords: StreamingWord[] = words
            .filter((word) => word.trim())
            .map((word) => ({
              id: `${assistantMessage.id}_${currentWordId++}`,
              text: `${word} `,
            }));

          // Update the assistant message with new content and streaming words
          setStreamingWords((prev) => [...(prev || []), ...newStreamingWords]);
        }
      }

      // Mark the assistant message as completed
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessage.id
            ? {
                ...msg,
                content: accumulatedContent,
                completed: true,
              }
            : msg,
        ),
      );
      setStreamingWords([]);
      if (tempId && tempId !== chatId) {
        navigate(`/app/${tempId}/chat`, { replace: true });
      }
    } catch (error) {
      console.error("Error:", error);
      setError(error instanceof Error ? error.message : JSON.stringify(error));

      // Update the assistant message with error
      setMessages((prev) =>
        prev.map((msg) =>
          msg.role === "assistant" && !msg.completed
            ? {
                ...msg,
                content:
                  "Sorry, an error occurred while processing your request. Please try again.",
                completed: true,
                streamingWords: undefined,
              }
            : msg,
        ),
      );
    }
  };

  return (
    <ChatInterface
      id={chatId}
      title={title}
      messages={messages}
      onSubmit={handleMessageSubmit}
      selectedModel={selectedModel}
      onModelChange={setSelectedModel}
      streamingWords={streamingWords}
    />
  );
}
